import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetRentalByIdQuery } from '../../../store/api/rentalApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

const RentalPrintBill = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: contractData, isLoading } = useGetRentalByIdQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();

  const currency = company?.currency || 'USD';
  const currencyDecimals = company?.currencyDecimals ?? 2;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(undefined, { 
      style: 'currency', 
      currency, 
      minimumFractionDigits: currencyDecimals, 
      maximumFractionDigits: currencyDecimals
    }).format(Number(amount) || 0);
  };

  if (isLoading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;
  if (!contractData) return <Typography sx={{ p: 3 }}>Rental contract not found.</Typography>;

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }} className="no-print">
        <Button variant="outlined" onClick={() => navigate('/rentals')}>Back to Rentals</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print Invoice
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 3 }} elevation={0} variant="outlined" id="print-area">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold">{company?.name || 'Company Name'}</Typography>
          <Typography variant="subtitle1" color="text.secondary">Rental Invoice</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Info Grid */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Bill To:</Typography>
            <Typography variant="body1" fontWeight="bold">{contractData.Customer?.name || 'N/A'}</Typography>
            {contractData.Customer?.phone && <Typography variant="body2">{contractData.Customer.phone}</Typography>}
            {contractData.Customer?.email && <Typography variant="body2">{contractData.Customer.email}</Typography>}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2" color="text.secondary">Contract Details:</Typography>
            <Typography variant="body2"><strong>Contract #:</strong> {contractData.contractNumber}</Typography>
            <Typography variant="body2"><strong>Date:</strong> {new Date(contractData.contractDate).toLocaleDateString()}</Typography>
            <Typography variant="body2"><strong>Start Date:</strong> {new Date(contractData.startDate).toLocaleDateString()}</Typography>
            <Typography variant="body2"><strong>End Date:</strong> {new Date(contractData.endDate).toLocaleDateString()}</Typography>
            <Typography variant="body2"><strong>Status:</strong> {contractData.status}</Typography>
          </Box>
        </Box>

        {/* Items Table */}
        <TableContainer sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell>Item Description</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractData.items?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.Product?.name || 'Unknown Item'}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.totalPrice)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 250 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Grand Total:</Typography>
              <Typography fontWeight="bold">{formatCurrency(contractData.grandTotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Deposit Received:</Typography>
              <Typography>{formatCurrency(contractData.depositAmount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Amount Paid:</Typography>
              <Typography>{formatCurrency(contractData.paidAmount)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight="bold">Balance Due:</Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {formatCurrency(Math.max(0, Number(contractData.grandTotal) - Number(contractData.paidAmount)))}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Footer Notes */}
        {contractData.notes && (
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed #ccc' }}>
            <Typography variant="subtitle2" color="text.secondary">Notes:</Typography>
            <Typography variant="body2">{contractData.notes}</Typography>
          </Box>
        )}
      </Paper>
      
      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { background: white; margin: 0; padding: 0; }
            #print-area { box-shadow: none !important; border: none !important; padding: 0 !important; }
          }
        `}
      </style>
    </Box>
  );
};

export default RentalPrintBill;
